import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Wrench, ClipboardList, Download, Calendar, DoorOpen, Gamepad2, LayoutList } from 'lucide-react';
import { toast } from 'sonner';
import { DATE_PRESETS, getPresetDates, downloadCsvFile } from '../lib/dateRange';

const PRESETS = DATE_PRESETS;

const SOURCE_LABELS = {
  GAME_MASTER_REPORT: 'Game Master Report',
  TECHNICIAN_DISCOVERY: 'Technician Discovery',
  PM_GENERATED: 'PM Generated',
  MANAGER_CREATED: 'Manager Created',
  UNKNOWN: 'Unclassified',
};

const fmtMin = (v) => (v === null || v === undefined ? '—' : `${v} min`);
const fmtCost = (v) => (v ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0');

const TAB_ENDPOINTS = {
  technician: '/reports/technician-performance',
  gm: '/reports/gm-performance',
  rooms: '/reports/room-analytics',
  assets: '/reports/recurring-issues',
  summary: '/reports/general-summary',
  backlog: '/reports/backlog',
};

const CSV_SLUGS = {
  technician: 'technician-performance',
  gm: 'gm-performance',
  rooms: 'room-analytics',
  assets: 'recurring-issues',
  summary: 'general-summary',
};

export default function ReportsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('technician');
  const [preset, setPreset] = useState('month');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  useEffect(() => {
    api.get('/branches').then(r => setBranches(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadReport(); }, [activeTab, preset, selectedBranch]); // eslint-disable-line

  const buildParams = () => {
    const dates = getPresetDates(preset);
    const params = {};
    if (dates.from) params.date_from = dates.from;
    if (dates.to) params.date_to = dates.to;
    if (selectedBranch !== 'all') params.branch_id = selectedBranch;
    return params;
  };

  const loadReport = async () => {
    setLoading(true);
    const params = activeTab === 'backlog'
      ? (selectedBranch !== 'all' ? { branch_id: selectedBranch } : {})
      : buildParams();
    try {
      const res = await api.get(TAB_ENDPOINTS[activeTab], { params });
      setData(prev => ({ ...prev, [activeTab]: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  };

  const exportCsv = async () => {
    const slug = CSV_SLUGS[activeTab];
    if (!slug) return;
    try {
      const { data: res } = await api.get(`/reports/${slug}/csv`, { params: buildParams() });
      downloadCsvFile(res.csv, res.filename);
    } catch { toast.error('Export failed'); }
  };

  const presetLabel = PRESETS.find(p => p.key === preset)?.label || '';
  const tech = data.technician, gm = data.gm, rooms = data.rooms, assets = data.assets;
  const summary = data.summary, backlog = data.backlog;

  const ExportBtn = () => (
    <Button variant="outline" size="sm" onClick={exportCsv} data-testid={`export-${activeTab}-csv`}>
      <Download className="w-3.5 h-3.5 me-1" /> CSV
    </Button>
  );

  const SubHeader = ({ text }) => (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">{text} — {presetLabel} <span className="opacity-60">(Asia/Riyadh)</span></p>
      <ExportBtn />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">Reports</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Performance &amp; analytics</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-[180px] h-9" data-testid="reports-branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-[160px] h-9" data-testid="reports-date-filter"><Calendar className="w-3.5 h-3.5 me-1.5 text-muted-foreground" /><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRESETS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="technician" className="gap-1.5" data-testid="tab-technician"><Users className="w-3.5 h-3.5" /> Technicians</TabsTrigger>
          <TabsTrigger value="gm" className="gap-1.5" data-testid="tab-gm"><Gamepad2 className="w-3.5 h-3.5" /> Game Masters</TabsTrigger>
          <TabsTrigger value="rooms" className="gap-1.5" data-testid="tab-rooms"><DoorOpen className="w-3.5 h-3.5" /> Rooms</TabsTrigger>
          <TabsTrigger value="assets" className="gap-1.5" data-testid="tab-assets"><Wrench className="w-3.5 h-3.5" /> Assets</TabsTrigger>
          <TabsTrigger value="summary" className="gap-1.5" data-testid="tab-summary"><LayoutList className="w-3.5 h-3.5" /> Summary</TabsTrigger>
          <TabsTrigger value="backlog" className="gap-1.5" data-testid="tab-backlog"><ClipboardList className="w-3.5 h-3.5" /> Backlog</TabsTrigger>
        </TabsList>

        {/* ---------- Technician Performance ---------- */}
        <TabsContent value="technician" className="space-y-4 mt-4">
          <SubHeader text="Assigned and completed work orders, average duration, and maintenance discovered by each technician (creator, not assignee)" />
          {tech && (
            <Card className="bg-card border-border shadow-sm">
              <CardContent className="p-0">
                {tech.note && (
                  <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-amber-600 dark:text-amber-400 text-sm">
                    {tech.note}
                  </div>
                )}
                <Table data-testid="tech-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Technician</TableHead>
                      <TableHead>Username</TableHead>
                      <TableHead className="text-end">{t('reports.assignedWOs')}</TableHead>
                      <TableHead className="text-end">{t('reports.completedWOs')}</TableHead>
                      <TableHead className="text-end">Avg Duration</TableHead>
                      <TableHead className="text-end">{t('reports.technicianDiscovery')}</TableHead>
                      <TableHead className="text-end">{t('reports.discoveredActive')}</TableHead>
                      <TableHead className="text-end">{t('reports.discoveredCompleted')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tech.rows.length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No data for this period</TableCell></TableRow>
                    )}
                    {tech.rows.map((r, i) => (
                      <TableRow key={r.technician_id} data-testid={`tech-row-${r.technician_id}`}>
                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="font-medium">{r.technician_name}</TableCell>
                        <TableCell className="text-muted-foreground">{r.technician_username || '—'}</TableCell>
                        <TableCell className="text-end">{r.assigned_count}</TableCell>
                        <TableCell className="text-end font-bold text-brand">{r.completed_count}</TableCell>
                        <TableCell className="text-end">{fmtMin(r.avg_duration_minutes)}</TableCell>
                        <TableCell className="text-end font-semibold text-violet-500" data-testid={`tech-discovery-${r.technician_id}`}>
                          {r.self_initiated_count}
                        </TableCell>
                        <TableCell className="text-end text-muted-foreground">{r.discovered_active_count}</TableCell>
                        <TableCell className="text-end text-muted-foreground">{r.discovered_completed_count}</TableCell>
                      </TableRow>
                    ))}
                    {tech.rows.length > 0 && (
                      <TableRow className="bg-muted/30 font-semibold">
                        <TableCell colSpan={3}>Total</TableCell>
                        <TableCell className="text-end">{tech.total_assigned}</TableCell>
                        <TableCell className="text-end text-brand">{tech.total}</TableCell>
                        <TableCell className="text-end">—</TableCell>
                        <TableCell className="text-end text-violet-500">{tech.total_self_initiated}</TableCell>
                        <TableCell className="text-end">—</TableCell>
                        <TableCell className="text-end">—</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---------- Game Master Performance ---------- */}
        <TabsContent value="gm" className="space-y-4 mt-4">
          <SubHeader text="Maintenance issues submitted, accepted, rejected and duplicate-blocked" />
          {gm && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Submitted', value: gm.total },
                  { label: 'Accepted', value: gm.total_accepted },
                  { label: 'Rejected', value: gm.total_rejected },
                  { label: 'Duplicate Blocked', value: gm.total_duplicate_blocked },
                ].map(s => (
                  <Card key={s.label} className="bg-card border-border shadow-sm">
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold text-foreground">{s.value}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-0">
                  <Table data-testid="gm-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead className="text-end">Submitted</TableHead>
                        <TableHead className="text-end">Accepted</TableHead>
                        <TableHead className="text-end">Rejected</TableHead>
                        <TableHead className="text-end">Dup. Blocked</TableHead>
                        <TableHead className="text-end">Acceptance %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gm.rows.length === 0 && (
                        <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No data for this period</TableCell></TableRow>
                      )}
                      {gm.rows.map((r, i) => (
                        <TableRow key={r.user_id}>
                          <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                          <TableCell className="font-medium">{r.name}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{r.role === 'requester' ? 'Game Master' : r.role}</Badge></TableCell>
                          <TableCell className="text-end font-bold text-brand">{r.total_submitted}</TableCell>
                          <TableCell className="text-end">{r.accepted}</TableCell>
                          <TableCell className="text-end">{r.rejected}</TableCell>
                          <TableCell className="text-end">{r.duplicate_blocked}</TableCell>
                          <TableCell className="text-end">{r.acceptance_rate}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ---------- Room Analytics ---------- */}
        <TabsContent value="rooms" className="space-y-4 mt-4">
          <SubHeader text="Work orders, average duration, cost and most recurring asset per room" />
          {rooms && (
            <Card className="bg-card border-border shadow-sm">
              <CardContent className="p-0">
                {rooms.unassigned_room_count > 0 && (
                  <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-amber-600 dark:text-amber-400 text-sm" data-testid="rooms-unassigned-note">
                    {t('reports.unassignedRoomNote', { count: rooms.unassigned_room_count })}
                  </div>
                )}
                <Table data-testid="rooms-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Room</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-end">WO Count</TableHead>
                      <TableHead className="text-end">Avg Duration</TableHead>
                      <TableHead className="text-end">Total Cost</TableHead>
                      <TableHead>Most Recurring Asset</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rooms.rows.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No data for this period</TableCell></TableRow>
                    )}
                    {rooms.rows.map((r, i) => (
                      <TableRow key={`${r.room_id || 'unassigned'}-${r.branch_name}`} data-testid={r.is_unassigned ? 'rooms-row-unassigned' : `rooms-row-${r.room_id}`}>
                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className={r.is_unassigned ? 'font-medium italic text-muted-foreground' : 'font-medium'}>
                          {r.is_unassigned ? t('reports.unassignedRoom') : r.room_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{r.branch_name}</TableCell>
                        <TableCell className="text-end font-bold text-brand">{r.wo_count}</TableCell>
                        <TableCell className="text-end">{fmtMin(r.avg_duration_minutes)}</TableCell>
                        <TableCell className="text-end">{fmtCost(r.total_cost)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {r.top_asset_name ? `${r.top_asset_name} (${r.top_asset_count})` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {rooms.rows.length > 0 && (
                      <TableRow className="bg-muted/30 font-semibold">
                        <TableCell colSpan={3}>Total</TableCell>
                        <TableCell className="text-end text-brand">{rooms.total}</TableCell>
                        <TableCell className="text-end">—</TableCell>
                        <TableCell className="text-end">{fmtCost(rooms.total_cost)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---------- Recurring Issues by Asset ---------- */}
        <TabsContent value="assets" className="space-y-4 mt-4">
          <SubHeader text="Work orders per asset (most problematic first)" />
          {assets && (
            <Card className="bg-card border-border shadow-sm">
              <CardContent className="p-0">
                <Table data-testid="assets-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Asset</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Room</TableHead>
                      <TableHead className="text-end">WO Count</TableHead>
                      <TableHead className="text-end">Avg Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assets.rows.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No data for this period</TableCell></TableRow>
                    )}
                    {assets.rows.map((r, i) => (
                      <TableRow key={r.asset_id}>
                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="font-mono text-xs">{r.asset_code || '—'}</TableCell>
                        <TableCell className="font-medium">{r.asset_name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{r.stage || '—'}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{r.room_name || '—'}</TableCell>
                        <TableCell className="text-end font-bold text-brand">{r.wo_count}</TableCell>
                        <TableCell className="text-end">{fmtMin(r.avg_duration_minutes)}</TableCell>
                      </TableRow>
                    ))}
                    {assets.rows.length > 0 && (
                      <TableRow className="bg-muted/30 font-semibold">
                        <TableCell colSpan={5}>Total</TableCell>
                        <TableCell className="text-end text-brand">{assets.total}</TableCell>
                        <TableCell className="text-end">—</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---------- General Summary ---------- */}
        <TabsContent value="summary" className="space-y-4 mt-4">
          <SubHeader text="Created, closed, active backlog, completion time and source breakdown" />
          {summary && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Created', value: summary.total_work_orders },
                  { label: 'Closed', value: summary.closed_count },
                  { label: 'Active Backlog', value: summary.active_backlog },
                  { label: 'Avg Completion', value: fmtMin(summary.avg_completion_minutes) },
                ].map(s => (
                  <Card key={s.label} className="bg-card border-border shadow-sm">
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold text-foreground" data-testid={`summary-${s.label.toLowerCase().replace(/ /g, '-')}`}>{s.value}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-card border-border shadow-sm">
                <CardHeader className="pb-2"><CardTitle className="text-base font-barlow">Breakdown by Source</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table data-testid="summary-source-table">
                    <TableHeader>
                      <TableRow><TableHead>Source</TableHead><TableHead className="text-end">Work Orders</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(summary.by_source || {}).map(([k, v]) => (
                        <TableRow key={k}>
                          <TableCell>{SOURCE_LABELS[k] || k}</TableCell>
                          <TableCell className="text-end font-bold">{v}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/30 font-semibold">
                        <TableCell>Total</TableCell>
                        <TableCell className="text-end text-brand">{summary.total_work_orders}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-card border-border shadow-sm">
                  <CardHeader className="pb-2"><CardTitle className="text-base font-barlow">By Status</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader><TableRow><TableHead>Status</TableHead><TableHead className="text-end">Count</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {Object.entries(summary.by_status || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                          <TableRow key={k}><TableCell><Badge variant="outline">{k}</Badge></TableCell><TableCell className="text-end font-bold">{v}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border shadow-sm">
                  <CardHeader className="pb-2"><CardTitle className="text-base font-barlow">By Priority</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader><TableRow><TableHead>Priority</TableHead><TableHead className="text-end">Count</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {Object.entries(summary.by_priority || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                          <TableRow key={k}><TableCell><Badge variant="outline">{k || '—'}</Badge></TableCell><TableCell className="text-end font-bold">{v}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ---------- Backlog ---------- */}
        <TabsContent value="backlog" className="space-y-4 mt-4">
          <p className="text-sm text-muted-foreground">Current open backlog by Kanban column (live state, no date filter)</p>
          {backlog && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { key: 'todo', label: 'To Do', color: 'text-blue-500 border-blue-500/30' },
                { key: 'doing', label: 'Doing', color: 'text-amber-500 border-amber-500/30' },
                { key: 'review', label: 'In Review', color: 'text-purple-500 border-purple-500/30' },
              ].map(col => (
                <Card key={col.key} className={`bg-card border shadow-sm ${col.color}`}>
                  <CardContent className="p-6 text-center">
                    <p className="text-4xl font-bold text-foreground" data-testid={`backlog-${col.key}`}>{backlog.columns[col.key]}</p>
                    <p className="text-sm text-muted-foreground mt-1">{col.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {backlog && (
            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-base font-barlow">By Status</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Status</TableHead><TableHead className="text-end">Count</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(backlog.by_status).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                      <TableRow key={status}>
                        <TableCell><Badge variant="outline">{status}</Badge></TableCell>
                        <TableCell className="text-end font-bold">{count}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-semibold">
                      <TableCell>Total Backlog</TableCell>
                      <TableCell className="text-end text-brand" data-testid="backlog-total">{backlog.total}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {loading && <div className="text-center text-muted-foreground py-8">Loading...</div>}
    </div>
  );
}
